import React from 'react';
import Image from 'frontastic/lib/image';
import Typography from 'components/commercetools-ui/atoms/typography';
import useMediaQuery from 'helpers/hooks/useMediaQuery';
import { tablet } from 'helpers/utils/screensizes';
import { Tiempos, NationalBook } from 'fonts';
import { GenericSectionContent } from '../../types';
import { useAdditionalCss } from '../../../helpers/useAdditionalCss';
import { sanitizeHtml, SectionCtas, SECTION_SPACING } from '../../../helpers/rendering';

// Cygnus ← Methven `tile`: a single full-bleed image with overlay (title + description + CTA).
// Desktop: left-aligned overlay. Mobile: black panel below image.
export default function CygnusRendering({ content }: { content: GenericSectionContent }) {
  const { cards = [], additional_css, additional_css_class_names } = content;
  const { uniqueClass, scopedAdditionalCss } = useAdditionalCss(additional_css, 'CygnusRendering');
  const [isTabletSize] = useMediaQuery(tablet);
  const card = cards[0];

  return (
    <>
      {scopedAdditionalCss && <style dangerouslySetInnerHTML={{ __html: scopedAdditionalCss }} />}
      <div className={`${SECTION_SPACING} relative w-full ${[additional_css_class_names, uniqueClass].filter(Boolean).join(' ')}`}>
        {card?.desktop_image?.url && (
          <div className="relative h-[18rem] md:h-[24rem] 2xl:h-[35rem]">
            <Image
              src={card.desktop_image.url}
              loading="eager"
              alt={card.desktop_image.description ?? card?.title ?? ''}
              fill
              style={{ objectFit: 'cover' }}
              className="brightness-75"
              height={500}
              width={500}
            />
          </div>
        )}
        {isTabletSize ? (
          <div className="absolute top-1/2 -translate-y-1/2 pl-160 text-left xl:left-0">
            {card?.title && (
              <div className="max-w-[34rem] xl:max-w-[42rem] 2xl:max-w-[56rem]">
                <Typography as="h1" className={`text-left text-34 font-semibold text-[#FFF] drop-shadow-sm md:leading-[3.8rem] xl:text-40 xl:leading-[4.2rem] 2xl:text-55 ${Tiempos.className}`}>
                  {card.title}
                </Typography>
              </div>
            )}
            {card?.description && (
              <div
                className={`mt-22 w-[60%] text-left text-18 font-medium leading-8 tracking-wide text-white 2xl:text-20 ${NationalBook.className}`}
                dangerouslySetInnerHTML={{ __html: sanitizeHtml(card.description) }}
              />
            )}
            {card?.ctas && card.ctas.length > 0 && <SectionCtas ctas={card.ctas} className="mt-22" />}
          </div>
        ) : (
          <div className="bg-black px-24 pb-63 pt-40">
            {card?.title && (
              <Typography as="h1" className="text-left text-34 font-semibold leading-tight text-white">
                {card.title}
              </Typography>
            )}
            {card?.description && (
              <div
                className="mt-22 text-left text-16 text-white md:text-20"
                dangerouslySetInnerHTML={{ __html: sanitizeHtml(card.description) }}
              />
            )}
            {card?.ctas && card.ctas.length > 0 && <SectionCtas ctas={card.ctas} className="mt-22" />}
          </div>
        )}
      </div>
    </>
  );
}
